import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GitCompare, Check, Minus, Plus, ShoppingCart, Eye, FileText } from "lucide-react";
import { useCompareMode } from "../lib/useCompareMode";
import { mediaVariant, mediaSrcSet, prefetchProduct } from "../lib/catalogueApi";
import { addToCart, serverMsg, capText } from "../lib/cartApi";
import { toast } from "sonner";
const PorPrice = () => <div className="text-base font-bold text-[var(--brand-primary)]" data-testid="price-por">Price on Request</div>;  // T66
import { StockPill } from "./StockPill";
import { isInCompare, toggleCompare } from "../lib/compare";
import { ProductTile, tileProps } from "./NoPhoto";
import { DemoBadge } from "./DemoBadge";
import { SavingLine } from "./ClearanceBits";
import { canBuy, maxQty } from "./product/BuyBox";

const CARD_SIZES = "(min-width: 1024px) 300px, (min-width: 768px) 33vw, 50vw";
const CHIP = "text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full leading-none";

// T60: online purchase only when sellable, priced, not reserved and with available stock (null = uncapped).
const tileBuyable = (p) => canBuy(p) && p.availability_state !== "reserved" && !p.clearance_reserved && p.availability_state !== "unavailable_order" && p.available_qty !== 0;

// T64: homepage tiles add 1 (qty is changed in the mini cart); compact "Add" pill beside the price
function TileAdd({ p }) {
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setBusy(true);
    try { await addToCart(p.id, 1); }
    catch (e) { toast.error(serverMsg(e, "Couldn't add this item to the cart.")); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={add} disabled={busy} data-testid={`card-add-${p.slug}`} aria-label={`Add ${p.display_name} to cart`}
      className="relative z-10 inline-flex h-11 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full bg-[var(--brand-button)] px-3 text-sm font-bold text-[var(--brand-on-button)] transition-colors hover:bg-[var(--brand-button-hover)] active:bg-[var(--brand-button-active)] disabled:opacity-50 motion-reduce:transition-none">
      <ShoppingCart size={16} aria-hidden="true" className="shrink-0" /><span className="truncate">{busy ? "Adding…" : "Add to cart"}</span>
    </button>
  );
}

export function TileBuy({ p }) {
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const max = maxQty(p);
  const add = async () => {
    setBusy(true);
    try { await addToCart(p.id, qty); setQty(1); }  // opens the mini cart drawer; clearance lines follow the cart's no-payment enquiry flow
    catch (e) { toast.error(serverMsg(e, "Couldn't add this item to the cart.")); }
    finally { setBusy(false); }
  };
  const step = "flex h-11 w-11 items-center justify-center text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-40 motion-reduce:transition-none";
  return (
    <div className="mt-auto px-4 pb-4" data-testid={`card-buy-${p.slug}`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex shrink-0 items-center justify-between rounded-full border border-slate-300" role="group" aria-label={`Quantity for ${p.display_name}`}>
          <button type="button" className={`${step} rounded-l-full`} aria-label="Decrease quantity" disabled={qty <= 1} onClick={() => setQty(qty - 1)} data-testid={`card-qty-dec-${p.slug}`}><Minus size={16} aria-hidden="true" /></button>
          <span className="w-7 text-center text-base font-semibold tabular-nums" aria-live="polite" data-testid={`card-qty-${p.slug}`}>{qty}</span>
          <button type="button" className={`${step} rounded-r-full`} aria-label="Increase quantity" disabled={qty >= max} onClick={() => setQty(qty + 1)} data-testid={`card-qty-inc-${p.slug}`}><Plus size={16} aria-hidden="true" /></button>
        </div>
        <button type="button" onClick={add} disabled={busy} data-testid={`card-add-${p.slug}`} aria-label={`Add ${p.display_name} to cart`}
          className="flex h-11 min-h-[44px] w-full min-w-0 shrink-0 items-center justify-center gap-1.5 rounded-full sm:w-auto sm:min-w-[72px] sm:flex-1 bg-[var(--brand-button)] px-3 text-sm font-bold text-[var(--brand-on-button)] transition-colors hover:bg-[var(--brand-button-hover)] active:bg-[var(--brand-button-active)] disabled:opacity-50 motion-reduce:transition-none">
          <ShoppingCart size={16} aria-hidden="true" className="shrink-0" /><span className="truncate">{busy ? "Adding…" : "Add"}</span>
        </button>
      </div>
      {qty >= max && p.available_qty != null && <p className="mt-1.5 text-xs text-slate-500" data-testid={`card-cap-${p.slug}`}>{capText(max)}</p>}
    </div>
  );
}

// T71: compare toggle lives in the tile's bottom action row and only shows in listing compare mode. Icon-only on phones.
function CompareToggle({ p }) {
  const [inCompare, setInCompare] = useState(false);
  useEffect(() => {
    const sync = () => setInCompare(isInCompare(p.slug));
    sync();
    window.addEventListener("compare-changed", sync);
    return () => window.removeEventListener("compare-changed", sync);
  }, [p.slug]);
  const onCompare = (e) => { e.preventDefault(); e.stopPropagation(); toggleCompare(p); setInCompare(isInCompare(p.slug)); };
  return (
    <button type="button" onClick={onCompare} aria-pressed={inCompare} aria-label={inCompare ? `Added to compare: remove ${p.display_name}` : `Add ${p.display_name} to compare`}
      data-testid={`card-compare-${p.slug}`} data-state={inCompare ? "added" : "idle"}
      className={`relative inline-flex h-11 w-11 shrink-0 items-center justify-center gap-1 rounded-full border text-xs font-bold transition-colors sm:w-auto sm:px-3 motion-reduce:transition-none max-sm:absolute max-sm:right-5 max-sm:top-5 max-sm:z-20 max-sm:shadow-md ${inCompare ? "border-[var(--brand-primary)] bg-[var(--brand-primary)] text-white" : "border-slate-300 bg-white text-slate-700 hover:border-[var(--brand-primary)] max-sm:border-slate-400 max-sm:bg-white/95 max-sm:backdrop-blur"}`}>
      <span className="relative inline-flex items-center justify-center" data-testid={`card-compare-icon-${p.slug}`} data-icon={inCompare ? "compare-tick" : "compare"}>
        <GitCompare size={16} aria-hidden="true" />
        {inCompare && <span className="absolute -bottom-1 -right-1.5 flex h-3 w-3 items-center justify-center rounded-full bg-emerald-500 ring-2 ring-[var(--brand-primary)]"><Check size={9} strokeWidth={4} className="text-white" aria-hidden="true" /></span>}
      </span>
      <span className="hidden sm:inline">{inCompare ? "Added" : "Compare"}</span>
    </button>
  );
}

// T71: one price line that never wraps + "inc GST" on its own reserved line, so the block is the same height at every width
const TilePrice = ({ inc }) => (inc ? (
  <div data-testid="price-inc">
    <div className="whitespace-nowrap text-base font-bold leading-6 tabular-nums text-[var(--brand-primary)] sm:text-lg sm:leading-7">${Number(inc).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
    <div className="text-[11px] font-medium leading-4 text-slate-500">inc GST</div>
  </div>
) : <div className="text-sm text-slate-500">Price on application</div>);

const ACT = "flex h-11 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full px-3 text-sm font-bold";

function TileAction({ p }) {
  if (tileBuyable(p)) return <TileAdd p={p} />;
  const quote = p.purchase_state === "price_on_request";
  return (
    <Link to={`/p/${p.slug}`} data-testid={`card-action-${p.slug}`} data-action={quote ? "quote" : "view"}
      className={`${ACT} border border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary-tint)]`}>
      {quote ? <FileText size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}<span className="truncate">{quote ? "Request a quote" : "View options"}</span>
    </Link>
  );
}

// T71: fixed anatomy so every tile in a row lines up: image, brand, name (2 lines), stock row, price block (reserved), action row (bottom).
export const ProductCard = ({ p, priority = false, buy = false }) => {
  const mode = useCompareMode();
  const showCompare = !buy && mode;
  return (
    <div className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-xl motion-reduce:transition-none" data-testid={`product-card-${p.slug}`} data-tile="">
      <Link to={`/p/${p.slug}`} onMouseEnter={() => prefetchProduct(p.slug)} onTouchStart={() => prefetchProduct(p.slug)} onFocus={() => prefetchProduct(p.slug)}
        className="flex flex-col" data-testid={`product-card-link-${p.slug}`}>
        <div className="relative m-3 mb-0">
          <ProductTile src={mediaVariant(p.primary_media_id, 320, "jpeg")} srcSet={mediaSrcSet(p.primary_media_id, "jpeg", [160, 320, 640])}
            webp={mediaSrcSet(p.primary_media_id, null, [160, 320, 640])} sizes={CARD_SIZES} width="320" height="320" alt={p.display_name} eager={priority}
            imgClass="transition-transform duration-300 group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
            {...tileProps(p)} nophotoTestid={`card-no-photo-${p.slug}`} testid={`card-image-${p.slug}`} />
          <div className={`pointer-events-none absolute left-2 top-2 z-10 flex max-w-[calc(100%-1rem)] flex-col items-start gap-1 ${showCompare ? "max-sm:max-w-[calc(100%-3.75rem)]" : ""}`} data-testid={`card-chips-${p.slug}`}>
            {p.badge && <span data-testid={`card-badge-${p.slug}`} className={`${CHIP} bg-[var(--brand-button)] text-[var(--brand-on-button)]`}>{p.badge}</span>}
            {p.is_bundle && <span data-testid={`card-bundle-${p.slug}`} className={`${CHIP} border border-[var(--brand-primary)] bg-white text-[var(--brand-primary)]`}>Bundle</span>}
            <DemoBadge p={p} className="!text-[10px]" testid={`card-demo-${p.slug}`} />
            {p.formerly && <span data-testid={`formerly-${p.id}`} className={`${CHIP} bg-slate-100 text-slate-600`}>{p.formerly}</span>}
          </div>
        </div>
        <div className="px-3 pt-3 sm:px-4">
          <div className="h-4 truncate text-[11px] font-semibold uppercase leading-4 tracking-wide text-[var(--brand-link)]" data-testid={`card-brand-${p.slug}`}>{p.brand_name || "\u00a0"}</div>
          <div className="mt-1 line-clamp-2 h-10 text-sm font-semibold leading-5" data-testid={`card-name-${p.slug}`}>{p.display_name}</div>
        </div>
      </Link>
      <div className="mt-2 flex h-7 min-w-0 items-center px-3 sm:px-4" data-testid={`card-stock-row-${p.slug}`}><StockPill p={p} testid={`stock-pill-${p.slug}`} className="!self-center max-w-full truncate !py-0.5 !text-xs !leading-4 whitespace-nowrap" /></div>
      <div className="mt-1.5 min-h-[56px] px-3 sm:min-h-[60px] sm:px-4" data-testid={`card-price-${p.slug}`}>
        {p.purchase_state === "price_on_request" ? <PorPrice /> : <TilePrice inc={p.price_inc_gst} />}
        <SavingLine p={p} testid={`card-saving-${p.slug}`} />
      </div>
      <div className="mt-auto flex items-center gap-2 px-3 pb-3 pt-2 sm:px-4 sm:pb-4" data-testid={`card-actions-${p.slug}`}>
        <TileAction p={p} />
        {showCompare && <CompareToggle p={p} />}
      </div>
    </div>
  );
};
